import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import SearchHeader from "../components/app/SearchHeader";
import SearchBar from "../components/app/SearchBar";
import SessionsSidebar from "../components/app/SessionsSidebar";
import ResultPanel from "../components/app/ResultPanel";
import ModelLeaderboard from "../components/app/ModelLeaderboard";
import { streamSSE } from "../lib/sse";

const DEFAULT_MODELS = [
  { id: "openai", label: "OpenAI GPT-5.4 Nano", provider: "OpenAI", model: "openai/gpt-5.4-nano" },
  { id: "anthropic", label: "Claude Opus 5", provider: "Anthropic", model: "anthropic/claude-opus-5" },
  { id: "google", label: "Gemini 3.1 Flash Lite", provider: "Google", model: "google/gemini-3.1-flash-lite" },
  { id: "xai", label: "Grok 4.5", provider: "xAI", model: "x-ai/grok-4.5" },
  { id: "deepseek", label: "DeepSeek R1", provider: "DeepSeek", model: "deepseek/deepseek-r1" },
];

export default function SearchPage() {
  const { user, refresh } = useAuth();
  const { t, lang } = useLang();
  const [models, setModels] = useState(DEFAULT_MODELS);
  const [history, setHistory] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState("");
  // Multi-turn conversation state.
  // turns = [{ search_id, query, attachments: [id], results: { [modelId]: ModelResult }, best_model }]
  const [turns, setTurns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bestModel, setBestModel] = useState(null);
  const [leaderboardKey, setLeaderboardKey] = useState(0);
  // Session id — pinned across searches so the backend can prepend prior
  // Q&A turns to each model's messages. Regenerated on "New" / logo click.
  const [sessionId, setSessionId] = useState(() =>
    (crypto.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`)
  );

  const credits = user?.credits ?? 0;
  // Admins search for free — they never run out of coins and the balance pill
  // is a different visual state (see HeroState).
  const outOfCoins = !user?.is_admin && credits < 1;

  const loadHistory = useCallback(async () => {
    try {
      const [{ data: h }, { data: s }] = await Promise.all([
        api.get("/history"),
        api.get("/sessions"),
      ]);
      setHistory(h);
      setSessions(s);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/models");
        if (Array.isArray(data) && data.length) setModels(data);
      } catch {
        /* keep defaults */
      }
      loadHistory();
    })();
  }, [loadHistory]);

  // Live-update model list when the admin applies changes (same tab + cross-tab).
  useEffect(() => {
    const applyDetail = (detail) => {
      if (Array.isArray(detail) && detail.length) {
        setModels(detail);
      } else {
        api.get("/models").then(({ data }) => {
          if (Array.isArray(data) && data.length) setModels(data);
        }).catch(() => { /* ignore */ });
      }
    };
    const onWin = (e) => applyDetail(e.detail);
    window.addEventListener("pq:models-updated", onWin);
    let bc;
    try {
      bc = new BroadcastChannel("prismquery");
      bc.addEventListener("message", (e) => {
        if (e.data?.type === "models-updated") applyDetail(e.data.detail);
      });
    } catch { /* ignore */ }
    return () => {
      window.removeEventListener("pq:models-updated", onWin);
      if (bc) bc.close();
    };
  }, []);

  const runSearch = async (q, attachmentIds = []) => {
    setLoading(true);
    setActiveId(null);
    setQuery(q);
    setBestModel(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    // Append a new pending turn: user question + one streaming AI result per model.
    const pendingResults = {};
    models.forEach((m) => {
      pendingResults[m.id] = {
        id: m.id,
        label: m.label,
        provider: m.provider,
        model: m.model,
        ok: false,
        content: "",
        error: "",
        latency_ms: 0,
        streaming: true,
      };
    });
    const newTurn = {
      search_id: null,
      query: q,
      attachments: attachmentIds,
      results: pendingResults,
      best_model: null,
    };
    setTurns((prev) => [...prev, newTurn]);

    const token = localStorage.getItem("pq_token");
    const params = new URLSearchParams({ query: q });
    if (attachmentIds.length) params.set("attachments", attachmentIds.join(","));
    if (lang) params.set("language", lang);
    if (sessionId) params.set("session_id", sessionId);
    const url = `${backendUrl}/api/search/stream?${params.toString()}`;

    const updateLastTurn = (updater) => {
      setTurns((prev) => {
        if (!prev.length) return prev;
        const last = prev[prev.length - 1];
        return [...prev.slice(0, -1), updater(last)];
      });
    };

    await new Promise((resolve) => {
      streamSSE(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        onEvent: (name, data) => {
          if (name === "chunk") {
            updateLastTurn((last) => {
              const cur = last.results[data.model_id];
              if (!cur) return last;
              return {
                ...last,
                results: {
                  ...last.results,
                  [data.model_id]: {
                    ...cur,
                    content: (cur.content || "") + data.content,
                    streaming: true,
                  },
                },
              };
            });
          } else if (name === "done") {
            updateLastTurn((last) => {
              const cur = last.results[data.model_id];
              if (!cur) return last;
              return {
                ...last,
                results: {
                  ...last.results,
                  [data.model_id]: { ...cur, ok: true, latency_ms: data.latency_ms, streaming: false },
                },
              };
            });
          } else if (name === "error") {
            updateLastTurn((last) => {
              const cur = last.results[data.model_id];
              if (!cur) return last;
              return {
                ...last,
                results: {
                  ...last.results,
                  [data.model_id]: {
                    ...cur,
                    ok: false,
                    error: data.error,
                    latency_ms: data.latency_ms,
                    streaming: false,
                  },
                },
              };
            });
          } else if (name === "meta_end") {
            setActiveId(data.search_id);
            updateLastTurn((last) => ({ ...last, search_id: data.search_id }));
            setTimeout(() => {
              if (data.refunded) {
                toast.success(
                  t("toast.refunded", { ok: models.length - data.fail_count, total: models.length }),
                  { duration: 6000 }
                );
              } else {
                toast.success(t("toast.compared", { ok: models.length - data.fail_count, total: models.length }));
              }
            }, 100);
            loadHistory();
            refresh();
          }
        },
        onError: (e) => {
          if (e?.message?.includes("402")) {
            toast.error(t("toast.insufficient"), {
              action: { label: t("toast.buyCoins"), onClick: () => (window.location.href = "/pricing") },
            });
          } else {
            toast.error(t("toast.searchFailed"));
          }
          refresh();
          resolve();
        },
        onDone: () => resolve(),
      });
    });
    setLoading(false);
  };

  const openHistory = async (id) => {
    // Kept for compatibility with any callers — opens the full session containing
    // this search rather than just the single search.
    try {
      const { data } = await api.get(`/history/${id}`);
      if (data.session_id) {
        await openSessionById(data.session_id, id);
      } else {
        // Legacy history entry with no session_id — render just this one turn.
        const rmap = {};
        (data.results || []).forEach((r) => (rmap[r.id] = r));
        setTurns([{
          search_id: id,
          query: data.query,
          attachments: data.attachments || [],
          results: rmap,
          best_model: data.best_model || null,
        }]);
        setActiveId(id);
        setQuery(data.query);
        setBestModel(data.best_model || null);
        setSessionId(crypto.randomUUID?.() || `s_${Date.now()}`);
      }
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch {
      toast.error(t("toast.historyLoadFail"));
    }
  };

  const openSessionById = async (sid, focusSearchId = null) => {
    try {
      const { data } = await api.get(`/sessions/${sid}`);
      const nextTurns = (data.turns || []).map((tt) => {
        const rmap = {};
        (tt.results || []).forEach((r) => (rmap[r.id] = { ...r, streaming: false }));
        return {
          search_id: tt.id,
          query: tt.query,
          attachments: tt.attachments || [],
          results: rmap,
          best_model: tt.best_model || null,
        };
      });
      setTurns(nextTurns);
      setSessionId(sid);
      const last = nextTurns[nextTurns.length - 1];
      setActiveId(focusSearchId || last?.search_id || null);
      setQuery(last?.query || "");
      setBestModel(last?.best_model || null);
    } catch {
      toast.error(t("toast.historyLoadFail"));
    }
  };

  const deleteHistory = async (id) => {
    try {
      await api.delete(`/history/${id}`);
      setHistory((h) => h.filter((it) => it.id !== id));
      if (activeId === id) {
        setActiveId(null);
        setTurns([]);
        setQuery("");
      }
      toast.success(t("toast.deleted"));
    } catch {
      toast.error(t("toast.deleteFail"));
    }
  };

  const openSession = async (session) => {
    if (!session?.session_id) return;
    await openSessionById(session.session_id);
  };

  const renameSession = async (sid, newTitle) => {
    try {
      await api.patch(`/sessions/${sid}`, { title: newTitle });
      setSessions((prev) =>
        prev.map((s) =>
          s.session_id === sid ? { ...s, title: newTitle, custom_title: true } : s
        )
      );
      toast.success(t("sessions.renamed"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("sessions.renameFail"));
    }
  };

  const deleteSession = async (sid) => {
    try {
      await api.delete(`/sessions/${sid}`);
      setSessions((prev) => prev.filter((s) => s.session_id !== sid));
      setHistory((prev) => prev.filter((h) => h.session_id !== sid));
      if (sessionId === sid) {
        newSearch();
      }
      toast.success(t("sessions.deleted"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("sessions.deleteFail"));
    }
  };

  const newSearch = () => {
    setActiveId(null);
    setTurns([]);
    setQuery("");
    setBestModel(null);
    // Fresh session — from here on the models forget everything previous.
    setSessionId(crypto.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  };

  const togglePin = async (modelId) => {
    if (!activeId) {
      toast.error(t("toast.saveFirst"));
      return;
    }
    const next = bestModel === modelId ? null : modelId;
    const prev = bestModel;
    setBestModel(next); // optimistic
    try {
      await api.post(`/searches/${activeId}/vote`, { model_id: next });
      setLeaderboardKey((k) => k + 1);
      if (next) toast.success(t("toast.pinned"));
    } catch (e) {
      setBestModel(prev); // revert
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("toast.pinFail"));
    }
  };

  const showHero = turns.length === 0 && !loading;

  return (
    <div className="min-h-screen w-full bg-[#0A0A0A] text-white flex">
      <SessionsSidebar
        sessions={sessions}
        activeSessionId={sessionId}
        onSelect={openSession}
        onRename={renameSession}
        onDelete={deleteSession}
        onNew={newSearch}
      />

      <main className="flex-1 min-w-0 flex flex-col">
        <SearchHeader onNewSession={newSearch} />

        {showHero ? (
          <HeroState
            models={models}
            credits={credits}
            outOfCoins={outOfCoins}
            loading={loading}
            onSearch={runSearch}
            leaderboardKey={leaderboardKey}
            t={t}
          />
        ) : (
          <ResultsFeed
            models={models}
            turns={turns}
            loading={loading}
            onSearch={runSearch}
            onNewSearch={newSearch}
            outOfCoins={outOfCoins}
            bestModel={bestModel}
            onTogglePin={togglePin}
            t={t}
          />
        )}
      </main>
    </div>
  );
}

/* ---------- Hero (empty / new search) ---------- */
function HeroState({ models, credits, outOfCoins, loading, onSearch, leaderboardKey, t }) {
  return (
    <section
      className="flex-1 flex items-center justify-center px-6 py-12 min-h-[calc(100vh-5rem)]"
      data-testid="empty-state"
    >
      <div className="w-full max-w-3xl text-center">
        <div className="label-eyebrow mb-4">{t("hero.eyebrow")}</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
          {t("hero.title1")}{" "}
          <span className="text-white/50">{t("hero.title2")}</span>
        </h1>
        <p className="text-white/60 text-[15px] leading-relaxed max-w-lg mx-auto mb-4">
          {t("hero.subtitle")}
        </p>
        <p className="text-white/40 text-[12px] leading-relaxed max-w-lg mx-auto mb-10 font-mono-p">
          {t("hero.footnote")}
        </p>

        <SearchBar
          variant="hero"
          onSearch={onSearch}
          loading={loading}
          disabled={outOfCoins}
          disabledReason={outOfCoins ? t("search.outOfCoins") : ""}
        />

        <div className="mt-10 flex flex-wrap justify-center gap-2">
          {models.map((m) => (
            <span
              key={m.id}
              className="label-eyebrow px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02]"
              data-testid={`model-chip-${m.id}`}
            >
              {m.label}
            </span>
          ))}
        </div>

        {credits <= 2 && (
          <div
            className="mt-12 mx-auto max-w-md p-4 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-between gap-4"
            data-testid="low-credit-banner"
          >
            <div className="flex items-center gap-3">
              <Coins className={`w-4 h-4 ${outOfCoins ? "text-[#ffb097]" : "text-white/60"}`} />
              <div className="text-left">
                <div className="label-eyebrow" style={{ color: outOfCoins ? "#ffb097" : undefined }}>
                  {outOfCoins ? t("balance.out") : t("balance.low")}
                </div>
                <div className="text-[13px] text-white/80">
                  {t("balance.left", { credits, plural: credits === 1 ? "" : "s" })}
                </div>
              </div>
            </div>
            <Link to="/pricing" className="btn-pill focus-ring" data-testid="empty-buy-credits">
              {t("nav.buy")}
            </Link>
          </div>
        )}

        <ModelLeaderboard refreshKey={leaderboardKey} />
      </div>
    </section>
  );
}

/* ---------- Results feed (stacked vertically) ---------- */
function ResultsFeed({ models, turns, loading, onSearch, onNewSearch, outOfCoins, bestModel, onTogglePin, t }) {
  const lastTurn = turns[turns.length - 1] || null;
  const composerInitial = lastTurn?.query || "";
  return (
    <>
      {/* Compact search bar above the feed */}
      <div className="sticky top-20 z-30 bg-[#0A0A0A]/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <SearchBar
              variant="compact"
              onSearch={onSearch}
              loading={loading}
              initialQuery={composerInitial}
              disabled={outOfCoins}
              disabledReason={outOfCoins ? t("search.outOfCoinsShort") : ""}
            />
          </div>
          <button
            onClick={onNewSearch}
            className="btn-ghost focus-ring flex-shrink-0"
            data-testid="new-search-inline-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            <span className="text-[11px] uppercase tracking-[0.15em] hidden sm:inline">{t("search.newInline")}</span>
          </button>
        </div>
      </div>

      {/* Turn count banner */}
      {turns.length > 0 && (
        <div className="max-w-3xl mx-auto w-full px-6 pt-8 pb-2">
          <div className="label-eyebrow" data-testid="session-turn-count">
            {t("sessions.turnCount", { count: turns.length, plural: turns.length === 1 ? "" : "s" })}
          </div>
        </div>
      )}

      {/* Stacked chat panels — one per model, each showing the full transcript */}
      <section
        className="max-w-3xl mx-auto w-full px-6 py-4 pb-24 flex flex-col gap-4"
        data-testid="results-grid"
      >
        {models.map((m) => {
          const modelTurns = turns.map((tt) => ({
            search_id: tt.search_id,
            query: tt.query,
            attachments: tt.attachments,
            result: tt.results?.[m.id] || null,
          }));
          return (
            <div key={m.id} className="border border-white/10 rounded-lg overflow-hidden bg-[#0A0A0A]">
              <ResultPanel
                model={m}
                turns={modelTurns}
                loading={loading}
                pinned={bestModel === m.id}
                onTogglePin={onTogglePin}
              />
            </div>
          );
        })}
      </section>
    </>
  );
}
